import React, { useMemo, useState, useEffect } from 'react';
import { HistoryItem, LightHistoryItem, NodeType } from '../types';
import { Image as ImageIcon, Video as VideoIcon, Sparkles, Images, Search, X, Undo2, Loader2 } from 'lucide-react';
import { resolveFileUrl, isFileReference } from '../utils/fileRegistry';
import { isLegacyMediaRef } from '../utils/mediaStorage';
import { historyItemFields } from '../utils/historyItems';
import { nodeKind, nodeTone } from './nodes/nodeTheme';

/**
 * The drawer on the right of the canvas: `ns-drawer`, 360px wide, from under the top bar to the
 * bottom. Storage (in App.tsx) and Change history are the same drawer.
 */
const DRAWER = 'ns-drawer absolute top-[72px] right-0 h-[calc(100%-72px)] w-[360px] max-w-[90vw] z-40 overflow-hidden animate-[ns-slide-up_240ms_cubic-bezier(0.34,1.45,0.5,1)]';

// Union type for history items (legacy and new format)
type AnyHistoryItem = HistoryItem | LightHistoryItem;

// Helper to get display data from either format
const getDisplayData = (item: AnyHistoryItem): {
    title?: string;
    prompt?: string;
    imageRef?: string;
    videoRef?: string;
    cost?: number;
} => {
    // Light or old, each field is read from the entry first, then from its data
    return historyItemFields(item);
};

// Component to resolve and display file:// references
const ThumbnailImage: React.FC<{ 
    src?: string; 
    userId?: string;
    type: 'image' | 'video';
}> = ({ src, userId, type }) => {
    const [resolvedUrl, setResolvedUrl] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    
    useEffect(() => {
        if (!src) {
            setResolvedUrl(null);
            return;
        }
        
        // References from the retired media backend cannot be resolved any more
        if (isLegacyMediaRef(src)) {
            setResolvedUrl(null);
            setIsLoading(false);
        }
        // If it's a file:// reference, resolve it
        else if (isFileReference(src) && userId) {
            setIsLoading(true);
            resolveFileUrl(userId, src)
                .then(url => {
                    setResolvedUrl(url);
                    setIsLoading(false);
                })
                .catch(e => {
                    console.warn('Failed to resolve file URL:', e);
                    setResolvedUrl(null);
                    setIsLoading(false);
                });
        } else if (src.startsWith('http') || src.startsWith('data:') || src.startsWith('blob:')) {
            // Direct URL - use as-is
            setResolvedUrl(src);
        } else {
            setResolvedUrl(null);
        }
    }, [src, userId]);
    
    if (isLoading) {
        return (
            <div className="w-full h-full flex items-center justify-center bg-cream-200">
                <Loader2 size={16} className="animate-spin text-ink-faint" />
            </div>
        );
    }
    
    if (!resolvedUrl) {
        return (
            <div className="w-full h-full flex items-center justify-center bg-cream-200 text-ink-faint">
                <ImageIcon size={24} />
            </div>
        );
    }
    
    if (type === 'video') {
        return <video src={resolvedUrl} className="w-full h-full object-cover" muted />;
    }
    
    return <img src={resolvedUrl} className="w-full h-full object-cover" alt="" />;
};

interface GalleryProps {
  history: AnyHistoryItem[];
  isOpen: boolean;
  onClose: () => void;
  onRestore: (item: AnyHistoryItem) => void;
  userId?: string;
}

export const Gallery: React.FC<GalleryProps> = ({ history, isOpen, onClose, onRestore, userId }) => {
  const [searchTerm, setSearchTerm] = React.useState('');
  // Everything, or pictures or videos only
  const [kind, setKind] = React.useState<'all' | 'image' | 'video'>('all');

  const historyItems = useMemo(() => {
    return history
      .filter(item => {
          const displayData = getDisplayData(item);
          // Only show items that have actual content
          if (item.type === NodeType.IMAGE_VIEWER && displayData.imageRef) return true;
          if (item.type === NodeType.IMAGE_GENERATOR && displayData.imageRef) return true;
          if (item.type === NodeType.VIDEO_GENERATOR && displayData.videoRef) return true;
          return false;
      })
      .filter(item => kind === 'all' || (kind === 'video') === (item.type === NodeType.VIDEO_GENERATOR))
      .filter(item => {
          const displayData = getDisplayData(item);
          const title = displayData.title || displayData.prompt || 'Untitled';
          return title.toLowerCase().includes(searchTerm.toLowerCase());
      })
      .sort((a, b) => b.timestamp - a.timestamp);
  }, [history, searchTerm, kind]);
  const totalCost = useMemo(() => historyItems.reduce((sum, item) => sum + (getDisplayData(item).cost ?? 0), 0), [historyItems]);

  if (!isOpen) return null;

  return (
    // The drawer of this side of the canvas, the same one as Storage and Change history: 360px of
    // glass under the top bar. (While it is open the app moves the zoom controls and the map left of it.)
    <div className={DRAWER} data-canvas-drawer="gallery">
      <div className="ns-drawer-head">
        <h2 className="ns-t-heading flex items-center gap-2">
            <Images size={16} className="text-ink-soft" />
            {/* The words of the button that opens it */}
            Media gallery
        </h2>
        <button onClick={onClose} className="ns-icon-btn" title="Close" aria-label="Close">
            <X />
        </button>
      </div>

      {/* Search, then the kind to show */}
      <div className="p-3 border-b border-cream-200 flex flex-col gap-2">
          <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-faint pointer-events-none" />
              <input
                type="text"
                placeholder="Search by name or prompt"
                aria-label="Search generations"
                className="ns-field ns-field-sm pl-9 py-1.5 rounded-full"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
          </div>
          <div className="flex items-center justify-between gap-2">
              <div className="ns-seg" role="group" aria-label="Kind">
                  {([['all', 'All'], ['image', 'Images'], ['video', 'Videos']] as const).map(([value, label]) => (
                      <button key={value} type="button" className="ns-seg-item" aria-pressed={kind === value} onClick={() => setKind(value)}>{label}</button>
                  ))}
              </div>
              <span className="ns-t-meta tabular-nums whitespace-nowrap">{historyItems.length}{totalCost > 0 ? ` · $${totalCost.toFixed(2)}` : ''}</span>
          </div>
      </div>

      {/* The pictures, newest first, two to a row */}
      <div className="flex-1 overflow-y-auto p-3">
        {historyItems.length === 0 ? (
            <div className="ns-empty py-10">
                <span className="ns-mark ns-mark-lg ns-mark-lavender"><Images /></span>
                <p className="ns-empty-title">{searchTerm || kind !== 'all' ? 'Nothing found' : 'Nothing generated yet'}</p>
                <p className="ns-empty-text">
                    {searchTerm || kind !== 'all' ? 'No image or video matches this search.' : 'Images and videos you generate in this workspace are listed here.'}
                </p>
            </div>
        ) : (
            <div className="grid grid-cols-2 gap-2.5">
            {historyItems.map((item, idx) => {
                const displayData = getDisplayData(item);
                const isVideo = item.type === NodeType.VIDEO_GENERATOR;
                const mediaSrc = isVideo ? displayData.videoRef : displayData.imageRef;
                const title = displayData.title || displayData.prompt || 'Untitled';
                return (
                    <button
                        type="button"
                        key={`${item.id}-${idx}`}
                        className="group relative text-left bg-white border border-cream-300 rounded-xl p-1.5 cursor-pointer hover:border-lavender hover:shadow-hair transition-colors"
                        onClick={() => onRestore(item)}
                        aria-label={`Put ${title} on the canvas`}
                        title={title}
                    >
                        <div className="aspect-[16/10] w-full bg-cream-200 rounded-lg overflow-hidden relative">
                            <ThumbnailImage
                                src={mediaSrc}
                                userId={userId}
                                type={isVideo ? 'video' : 'image'}
                            />

                            {/* Type Badge: in the colour of the kind of node it came from */}
                            <div data-tone={nodeTone(item.type)} className="ns-badge ns-badge-tone absolute top-1.5 left-1.5 shadow-hair">
                                {item.type === NodeType.VIDEO_GENERATOR && <VideoIcon size={12} />}
                                {item.type === NodeType.IMAGE_GENERATOR && <Sparkles size={12} />}
                                {item.type === NodeType.IMAGE_VIEWER && <ImageIcon size={12} />}
                                {/* What the canvas calls the kind: Image, Video */}
                                <span>{nodeKind(item.type).label}</span>
                            </div>

                            {/* On hover: what a click does */}
                            <div className="absolute inset-0 bg-ink/20 opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 transition-opacity flex items-center justify-center">
                                <span className="ns-glass-dark rounded-full px-3 py-1 text-xs font-semibold flex items-center gap-1.5">
                                    <Undo2 size={14} /> Put on canvas
                                </span>
                            </div>
                        </div>

                        <div className="pt-1.5 px-0.5">
                            <div className="text-xs font-semibold text-ink truncate">{title}</div>
                            <div className="ns-t-meta flex justify-between gap-2">
                                 <span>{new Date(item.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                 {displayData.cost !== undefined && (
                                     <span className="text-ok-deep tabular-nums">${displayData.cost.toFixed(3)}</span>
                                 )}
                            </div>
                        </div>
                    </button>
                );
            })}
            </div>
        )}
      </div>
    </div>
  );
};
